import type { I18n } from '@lingui/core';
import { msg } from '@lingui/core/macro';
import { z } from 'zod';

import {
  resolveAgentText,
  resolveScreenGuide,
  toolName,
  type Navigation,
  type Permission,
  type ScreenContext,
  type ScreenGuide,
  type ScreenManifest,
  type ScreenRegistry,
} from '@/shared/screen';

import { EMPTY_INPUT_SCHEMA, toolFailure, toolText, type AgentTool } from './agent-tool';
import { screenActionTools, screenTurnNotes, type ScreenActionDeps } from './screen-actions';

export interface MetaToolDeps {
  registry: ScreenRegistry;
  i18n: I18n;
  nav: Navigation;
  visible: (screenId: string) => boolean;
  /** O contexto da tela aberta, sem máscara: vai só ao Luy, no `get_screen_guide` (U56, L09). */
  currentContext: () => (ScreenContext & { screenId: string }) | null;
  /** O mesmo, mascarado, para o `get_screen_context`, que também vai ao WebMCP do navegador (U24). */
  maskedContext?: () => (ScreenContext & { screenId: string }) | null;
  /** O manifesto que a tela montada registrou: liga `get_screen_actions` e `run_screen_action` (U56). */
  mountedScreen?: (screenId: string) => ScreenManifest | undefined;
  /** Tela montada e acessível agora (U46): o `get_screen_guide` sem `screenId` fala dela. */
  activeScreen?: () => string | null;
  /** Ferramentas registradas agora para a tela. */
  screenTools?: (screenId: string) => readonly AgentTool[];
  /** Permissão de ação de quem vê: o guia só cita o que a pessoa pode fazer. */
  canUse?: (permission: Permission | undefined) => boolean;
}

const DESCRIPTIONS = {
  list: msg`Lista as telas do UYZY que a pessoa pode abrir, com id, título e para que serve cada uma. A tela que a pessoa está vendo agora vem primeiro, com current: true, mesmo que não esteja no menu.`,
  open: msg`Abre uma tela pelo id (veja list_screens). As ferramentas dela ficam disponíveis depois que ela abre.`,
  context: msg`Devolve o que a tela atual declara sobre si: resumo, registro e campos não sensíveis.`,
  guide: msg`Explica a tela atual, ou a de um screenId de list_screens: para que serve, seções, campos, status e quando usar cada ação. Traz junto o contexto atual e as ferramentas que dá para chamar agora.`,
};

export function metaTools(deps: MetaToolDeps): AgentTool[] {
  const available = (screen: ScreenManifest | undefined): screen is ScreenManifest =>
    !!screen && screen.agent !== false && deps.visible(screen.id);
  const { mountedScreen, activeScreen } = deps;
  const actionDeps: ScreenActionDeps | null = activeScreen
    ? {
        registry: deps.registry,
        i18n: deps.i18n,
        nav: deps.nav,
        visible: deps.visible,
        // Sem verificador de permissão, nada passa (como no guia).
        canUse: deps.canUse ?? (() => false),
        activeScreen,
        mountedScreen: mountedScreen ?? (() => undefined),
      }
    : null;
  // A tela que a pessoa vê agora vai em toda declaração do turno (U56): sem ela, o modelo não sabe onde está.
  const notes = actionDeps ? { full: () => screenTurnNotes(actionDeps).full, short: () => screenTurnNotes(actionDeps).short } : null;
  const list = (): AgentTool => ({
    name: 'list_screens',
    description: deps.i18n._(DESCRIPTIONS.list),
    inputSchema: EMPTY_INPUT_SCHEMA,
    readOnly: true,
    scope: 'app',
    origin: {},
    ...(notes ? { turnNote: notes.short } : {}),
    execute: () => {
      const active = activeScreen?.() ?? null;
      const row = (s: ScreenManifest) => ({
        id: s.id,
        title: deps.i18n._(s.title),
        description: resolveAgentText(deps.i18n, s.description),
        ...(s.id === active ? { current: true } : {}),
      });
      const menu = deps.registry.screens.filter((s) => s.nav && s.agent !== false && deps.visible(s.id) && s.id !== active);
      const current = active === null ? undefined : deps.registry.byId(active);
      // A tela aberta entra mesmo fora do menu (rota filha, com `$`): sem ela, o modelo conclui que não existe.
      const rows = [...(available(current) ? [current] : []), ...menu].map(row);
      return Promise.resolve(toolText(JSON.stringify(rows)));
    },
  });
  const openInput = z.object({ screenId: z.string() });
  const open = (): AgentTool => ({
    name: 'open_screen',
    description: deps.i18n._(DESCRIPTIONS.open),
    inputSchema: z.toJSONSchema(openInput),
    readOnly: true,
    scope: 'app',
    origin: {},
    ...(notes ? { turnNote: notes.short } : {}),
    execute: async (raw, options) => {
      const parsed = openInput.safeParse(raw);
      if (!parsed.success) return toolFailure('screenId is required');
      const screen = deps.registry.byId(parsed.data.screenId);
      if (!available(screen)) return toolFailure(`Screen ${parsed.data.screenId} is not available`);
      if (screen.id === activeScreen?.())
        return toolText(`${deps.i18n._(screen.title)} (${screen.id}) is already open: there is no need to open it again. Act on it with get_screen_actions.`);
      if (options?.signal?.aborted) return toolFailure('Cancelled before opening the screen.');
      await deps.nav.to(screen.path);
      return toolText(`Opened ${deps.i18n._(screen.title)}`);
    },
  });
  const context = (): AgentTool => ({
    name: 'get_screen_context',
    description: deps.i18n._(DESCRIPTIONS.context),
    inputSchema: EMPTY_INPUT_SCHEMA,
    readOnly: true,
    scope: 'app',
    origin: {},
    execute: () => {
      const ctx = deps.maskedContext ? deps.maskedContext() : deps.currentContext();
      return Promise.resolve(ctx ? toolText(JSON.stringify(ctx)) : toolText('No screen context declared.'));
    },
  });
  const guideInput = z.strictObject({ screenId: z.string().optional() });
  const guide = (): AgentTool => ({
    name: 'get_screen_guide',
    description: deps.i18n._(DESCRIPTIONS.guide),
    inputSchema: z.toJSONSchema(guideInput),
    readOnly: true,
    scope: 'app',
    origin: {},
    internal: true,
    ...(notes ? { turnNote: notes.full } : {}),
    execute: async (raw) => {
      const parsed = guideInput.safeParse(raw);
      if (!parsed.success) return toolFailure(`Invalid input: ${parsed.error.message}`);
      const active = deps.activeScreen?.() ?? null;
      const id = parsed.data.screenId ?? active;
      if (id === null) return toolFailure('No screen is open. Pass a screenId from list_screens.');
      const screen = deps.registry.byId(id);
      if (!available(screen)) return toolFailure(`Screen ${id} is not available`);
      const isOpen = id === active;
      const found = await deps.registry.loadGuide(id);
      const tools = isOpen ? (deps.screenTools?.(id) ?? []) : [];
      const ctx = isOpen ? deps.currentContext() : null;
      return toolText(
        JSON.stringify({
          screenId: id,
          title: deps.i18n._(screen.title),
          description: resolveAgentText(deps.i18n, screen.description),
          open: isOpen,
          guide: found ? guideFor(deps, screen, found, tools, available) : null,
          context: ctx?.screenId === id ? ctx : null,
          tools: tools.map((t) => ({ name: t.name, description: t.description, readOnly: t.readOnly, needsConfirmation: !t.readOnly })),
        }),
      );
    },
  });
  const actions = actionDeps && mountedScreen ? screenActionTools(actionDeps) : [];
  return [list(), open(), context(), guide(), ...actions];
}

/** O guia traduzido, só com as ações que a pessoa pode usar e as telas relacionadas que ela vê. */
function guideFor(
  deps: MetaToolDeps,
  screen: ScreenManifest,
  guide: ScreenGuide,
  tools: readonly AgentTool[],
  available: (s: ScreenManifest | undefined) => s is ScreenManifest,
) {
  const resolved = resolveScreenGuide(deps.i18n, guide);
  // Sem verificador de permissão, nada passa (revisão da F6, B2).
  const canUse = deps.canUse ?? (() => false);
  const allowed = new Map(screen.actions.filter((a) => canUse(a.permission)).map((a) => [a.id, a]));
  const registered = new Set(tools.map((t) => t.origin.actionId));
  return {
    ...resolved,
    actions: resolved.actions.flatMap((a) => {
      const declared = allowed.get(a.id);
      if (!declared) return [];
      return [{ ...a, label: deps.i18n._(declared.label), kind: declared.kind, tool: toolName(a.id), availableNow: registered.has(a.id) }];
    }),
    related: (guide.related ?? []).flatMap((rid) => {
      const other = deps.registry.byId(rid);
      return available(other) ? [{ id: rid, title: deps.i18n._(other.title) }] : [];
    }),
  };
}
